import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Stack,
  Button,
} from '@chakra-ui/react';
import {
  MdFilter
} from 'react-icons/md';
import {
  BiRuler
} from 'react-icons/bi';
import {
  AiOutlinePlusCircle
} from 'react-icons/ai';
import {
  GiMaterialsScience
} from 'react-icons/gi';
import {
  FaRegClone
} from 'react-icons/fa';
import { RiAuctionLine } from 'react-icons/ri';
import { useListing, useDemotedListing, useMarkListingAsFake, useMarkListingAsSet, useMarkListingAsOriginal, useRejectListing, useUpdateListing, useMigrateListingImage, useMigrateDemotedListingImage } from 'hooks/queries/useListings';
import { useItem, useCreateItemFromListing } from 'hooks/queries/useItems';
import { useItemSearch } from 'hooks/queries/useSearch';
import { useCreateGrouping, useUnlinkListing } from 'hooks/queries/useGroupings';
import { useListingSuggestedItems } from 'hooks/queries/useListingSuggestedItems';
import { useMaterials } from 'hooks/queries/useMaterials';
import { useArtist } from 'hooks/queries/useArtists';
import { NavigationService } from 'services';
import { ItemInfoBlock, Slider, ItemGrid, ItemCard, TabGroup, Modal, GroupingCreationWizard, ListingDataSuggestionWizard, ListingContainer, Loader, BasicListingCard, MigrateImageWizard } from 'components';
import ListingEditForm, { ListingEditFormData, ListingEditFormOption } from 'components/Form/ListingEditForm/ListingEditForm';
import { ListingStatus } from 'models/Listing/types';
import { ListingModel, ItemModel, ArtistModel } from 'models';
import { JSONObject } from 'models/Api/types';
import { createPreviewItemFromListing } from 'utils/listing';

interface ListingDetailsViewModalState {
  itemSuggestionWizard: boolean;
  markAsFake: boolean;
  markAsSet: boolean;
  markAsOriginal: boolean;
  reject: boolean;
  editListing: boolean;
  listingEditFieldModal: ListingEditFormOption | undefined;
  createItem: boolean;
  migrateImage: boolean;
}

export default function ListingDetailsView() {
  const { id: listingId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  // Modal state management
  const [modals, setModals] = useState<ListingDetailsViewModalState>({
    itemSuggestionWizard: false,
    markAsFake: false,
    markAsSet: false,
    markAsOriginal: false,
    reject: false,
    editListing: false,
    listingEditFieldModal: undefined,
    createItem: false,
    migrateImage: false,
  });

  // Check if this is a demoted listing
  const urlParams = new URLSearchParams(location.search);
  const isDemoted = urlParams.get('type') === 'demoted';

  // Fetch listing data
  const { data: regularListing, isLoading: regularListingLoading } = useListing(listingId!, { enabled: !isDemoted });
  const { data: demotedListing, isLoading: demotedListingLoading } = useDemotedListing(listingId!, { enabled: isDemoted });

  const listing = isDemoted ? demotedListing : regularListing;
  const listingLoading = isDemoted ? demotedListingLoading : regularListingLoading;

  // Fetch materials for editing
  const { data: materials } = useMaterials();

  // Fetch artist data
  const { data: artist } = useArtist(listing?.artistId || '', { enabled: !!listing?.artistId && !isDemoted });

  // Fetch associated item if it exists
  // First check if the listing has the full item object
  const associatedItemFromListing = listing?.getAssociatedItem();

  // If not, get the item ID and fetch it separately
  const associatedItemId = listing?.hasAssociatedItem() && !associatedItemFromListing
    ? listing.getItemIds()[0]
    : undefined;

  const { data: fetchedAssociatedItem } = useItem(associatedItemId!, {
    enabled: !!associatedItemId && !isDemoted
  });

  // Use whichever is available
  const associatedItem = associatedItemFromListing || fetchedAssociatedItem;

  // Fetch item suggestions if listing is pending and has no associated item
  const shouldFetchSuggestions = listing?.processingStatus === ListingStatus.PENDING && !listing?.hasAssociatedItem() && !isDemoted;

  // Use item search with listing display name and artist
  const searchQuery = listing ? {
    query: listing.getDisplayName(),
    artistId: [listing.artistId]
  } : undefined;

  // Intelligence API suggestions
  const { data: suggestedItemsData, isLoading: intelligenceSuggestionsLoading } = useListingSuggestedItems(listingId!, shouldFetchSuggestions && !isDemoted);

  // Search-based suggestions (fallback)
  const { data: searchBasedSuggestions, isLoading: searchSuggestionsLoading } = useItemSearch(
    shouldFetchSuggestions && !isDemoted ? searchQuery : undefined,
    1
  );

  // Use intelligence API results, fallback to search results, or empty array
  const intelligenceItems = suggestedItemsData?.suggestions?.map(s => s.item) ?? [];
  const suggestedItems = intelligenceItems.length > 0 ? intelligenceItems : (searchBasedSuggestions?.data || []);
  const suggestedItemsLoading = intelligenceSuggestionsLoading || searchSuggestionsLoading;

  // Create grouping mutation
  const createGroupingMutation = useCreateGrouping();

  // Listing marking mutations
  const markAsFakeMutation = useMarkListingAsFake();
  const markAsSetMutation = useMarkListingAsSet();
  const markAsOriginalMutation = useMarkListingAsOriginal();
  const rejectListingMutation = useRejectListing();

  // Listing update mutation
  const updateListingMutation = useUpdateListing();

  // Create item mutation
  const createItemFromListingMutation = useCreateItemFromListing();

  // Migrate listing image mutations - use appropriate one based on demoted status
  const migrateListingImageMutation = useMigrateListingImage();
  const migrateDemotedListingImageMutation = useMigrateDemotedListingImage();

  // Unlink listing mutation
  const unlinkListingMutation = useUnlinkListing();

  // Modal handlers
  const onItemSuggestionWizardOpen = () => {
    setModals(prev => ({ ...prev, itemSuggestionWizard: true }));
  };

  const onItemSuggestionWizardClose = () => {
    setModals(prev => ({ ...prev, itemSuggestionWizard: false }));
  };

  // Handler for wizard submission
  const handleGroupingWizardSubmit = (item: ItemModel, listings: ListingModel[]) => {
    if (!listings.length) {
      return;
    }

    createGroupingMutation.mutate({
      item_id: item.id,
      listings: listings.map((listing) => listing.id),
      auto_link: true
    });
    onItemSuggestionWizardClose();
  };

  const renderCreateItemModalContent = () => {
    if (!listing || !createItemPreview) {
      return <Box />;
    }

    return (
      <Stack spacing={6} align="center">
        <BasicListingCard listing={listing} showLabel={true} />
        <ItemCard item={createItemPreview} size="md" showMetadata={true} defaultMetadataExpanded={true} />
        <Button colorScheme="blue" onClick={handleCreateItemSubmit} isLoading={createItemFromListingMutation.isPending}>
          Create Item
        </Button>
      </Stack>
    );
  };

  // Render functions
  const renderListingInfoSlider = () => {
    if (!listing) return null;

    const truncateText = (text: string | undefined, maxLength: number = 80) => {
      if (!text) return text;
      return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
    };

    const infoBlocks = [
      { label: 'Auction House', value: listing.source, icon: RiAuctionLine, field: undefined },
      { label: 'Dimensions', value: listing.getDimensionsForDisplay(), icon: BiRuler, field: 'dimensions' as ListingEditFormOption },
      { label: 'Material', value: truncateText(listing.material), icon: GiMaterialsScience, field: 'materialIds' as ListingEditFormOption },
      { label: 'Edition', value: listing.edition, icon: FaRegClone, field: 'edition' as ListingEditFormOption, hideForOriginal: true },
      { label: 'Type', value: listing.type, icon: MdFilter, field: 'type' as ListingEditFormOption },
    ];

    // Filter out edition block if listing is original
    const filteredBlocks = infoBlocks.filter(block => {
      if (block.hideForOriginal && listing.isOriginal()) {
        return false;
      }
      return true;
    });

    const content = filteredBlocks.map((block, index: number) => (
      <ItemInfoBlock
        key={`listing_info_block_${index}`}
        label={block.label}
        value={block.value}
        icon={block.icon}
        fallbackIcon={AiOutlinePlusCircle}
        onFallbackContentClick={block.field ? () => onListingEditFieldModalOpen(block.field!) : undefined}
        editable={!!block.field}
        onEditClick={block.field ? () => onListingEditFieldModalOpen(block.field!) : undefined}
        isLoading={listingLoading}
      />
    ));

    return (
      <Box marginTop="40px">
        <Slider content={content} slidesPerView={4} enableAutoPlay={false} slidesPerMobileView={2} />
      </Box>
    );
  };

  const renderTabSection = () => {
    const labels: string[] = [];
    const content: JSX.Element[] = [];

    // Associated Item Tab
    if (associatedItem) {
      labels.push('Associated Item');
      content.push(
        <Stack spacing={4} alignItems="center">
          <ItemCard
            item={associatedItem}
            onClick={() => navigate(NavigationService.getItemDetailsPath(associatedItem.id, associatedItem.getDisplayName()))}
            size="md"
            clickOnImage
            clickOnName
            showMetadata
          />
        </Stack>
      );
    }

    // Item Suggestions Tab
    if (shouldFetchSuggestions) {
      labels.push('Item Suggestions');
      content.push(
        <ItemGrid
          items={suggestedItems}
          isLoading={suggestedItemsLoading}
          gridDisplay={true}
          showFiltersLabel={false}
          onItemNameClick={(item) => navigate(NavigationService.getItemDetailsPath(item.id, item.getDisplayName()))}
          onArtistNameClick={(item) => navigate(NavigationService.getArtistDetailsPath(item.artistId, item.getArtistName()))}
          showMetadata={true}
        />
      );
    }

    // Don't render if no tabs
    if (labels.length === 0) {
      return null;
    }

    return <TabGroup labels={labels} content={content} startingTab={0} />;
  }

  const renderMigrateImageModalContent = () => {
    if (!listing) return <Box />;

    const mutation = isDemoted ? migrateDemotedListingImageMutation : migrateListingImageMutation;

    return (
      <MigrateImageWizard
        entity={listing}
        entityType="listing"
        isLoading={mutation.isPending}
        onSubmit={handleMigrateImageSubmit}
        hasErrors={mutation.isError}
      />
    );
  };

  const renderModals = () => {
    return (
      <>
        {listing && (
          <Modal
            title="Select Item for Grouping"
            content={
              <GroupingCreationWizard
                defaultArtistId={listing.artistId}
                defaultItem={associatedItem || null}
                defaultListings={[listing]}
                preselectedListings={[listing]}
                initialWorkflow="listing-first"
                onSubmit={handleGroupingWizardSubmit}
                isLoading={createGroupingMutation.isPending}
                itemSuggestions={suggestedItems}
              />
            }
            isOpen={modals.itemSuggestionWizard}
            onClose={onItemSuggestionWizardClose}
            showCloseButton={false}
            showSubmitButton={false}
            size="4xl"
          />
        )}
        <Modal
          title="Create Item"
          content={renderCreateItemModalContent()}
          isOpen={modals.createItem}
          onClose={onCreateItemModalClose}
          showCloseButton={false}
          showSubmitButton={false}
          size="3xl"
        />
        <Modal
          title="Mark as Fake"
          content={
            <ListingDataSuggestionWizard
              listing={listing}
              initialSelection="FAKE"
              onSubmit={handleMarkingWizardSubmit}
              isLoading={markAsFakeMutation.isPending}
            />
          }
          isOpen={modals.markAsFake}
          onClose={() => setModals(prev => ({ ...prev, markAsFake: false }))}
          showCloseButton={false}
          showSubmitButton={false}
          size="2xl"
        />
        <Modal
          title="Mark as Set"
          content={
            <ListingDataSuggestionWizard
              listing={listing}
              initialSelection="MULTIPLE_ITEMS"
              onSubmit={handleMarkingWizardSubmit}
              isLoading={markAsSetMutation.isPending}
            />
          }
          isOpen={modals.markAsSet}
          onClose={() => setModals(prev => ({ ...prev, markAsSet: false }))}
          showCloseButton={false}
          showSubmitButton={false}
          size="2xl"
        />
        <Modal
          title="Mark as Original"
          content={
            <ListingDataSuggestionWizard
              listing={listing}
              initialSelection="ORIGINAL"
              onSubmit={handleMarkingWizardSubmit}
              isLoading={markAsOriginalMutation.isPending}
            />
          }
          isOpen={modals.markAsOriginal}
          onClose={() => setModals(prev => ({ ...prev, markAsOriginal: false }))}
          showCloseButton={false}
          showSubmitButton={false}
          size="2xl"
        />
        <Modal
          title="Reject Listing"
          content={
            <ListingDataSuggestionWizard
              listing={listing}
              initialSelection="REJECT"
              onSubmit={handleMarkingWizardSubmit}
              isLoading={rejectListingMutation.isPending}
            />
          }
          isOpen={modals.reject}
          onClose={() => setModals(prev => ({ ...prev, reject: false }))}
          showCloseButton={false}
          showSubmitButton={false}
          size="2xl"
        />
        <Modal
          title="Edit Listing"
          content={
            <ListingEditForm
              listing={listing}
              materialList={materials}
              defaultFormData={getDefaultFormData()}
              onSubmit={handleEditFormSubmit}
              submitLoading={updateListingMutation.isPending}
            />
          }
          isOpen={modals.editListing}
          onClose={onEditModalClose}
          showCloseButton={true}
          showSubmitButton={false}
          size="2xl"
        />
        <Modal
          isOpen={!!modals.listingEditFieldModal}
          onClose={onListingEditFieldModalClose}
          title="Edit Listing"
          content={
            <ListingEditForm
              listing={listing}
              materialList={materials}
              defaultFormData={getDefaultFormData()}
              displayOptions={modals.listingEditFieldModal ? [modals.listingEditFieldModal] : undefined}
              requiredOptions={modals.listingEditFieldModal ? [modals.listingEditFieldModal] : undefined}
              onSubmit={handleEditFormSubmit}
              submitLoading={updateListingMutation.isPending}
            />
          }
          showCloseButton={false}
          showSubmitButton={false}
        />
        <Modal
          isOpen={modals.migrateImage}
          onClose={onMigrateImageModalClose}
          title="Migrate Listing Image"
          content={renderMigrateImageModalContent()}
          showCloseButton={false}
          showSubmitButton={false}
          size="2xl"
        />
      </>
    );
  }

  // Action handlers
  const handleViewSource = () => {
    if (listing?.sourceLink) {
      window.open(listing.sourceLink, '_blank');
    }
  };

  const handleCreateItem = () => {
    setModals(prev => ({ ...prev, createItem: true }));
  };

  const handleMarkAsOriginal = () => {
    setModals(prev => ({ ...prev, markAsOriginal: true }));
  };

  const handleMarkAsSet = () => {
    setModals(prev => ({ ...prev, markAsSet: true }));
  };

  const handleMarkAsFake = () => {
    setModals(prev => ({ ...prev, markAsFake: true }));
  };

  const handleReject = () => {
    setModals(prev => ({ ...prev, reject: true }));
  };

  const handleMarkingWizardSubmit = (listing: ListingModel, data: JSONObject) => {
    if (data.authentic === false) {
      markAsFakeMutation.mutate([listing.id]);
      setModals(prev => ({ ...prev, markAsFake: false }));
    } else if (data.multiple_items === true) {
      markAsSetMutation.mutate([listing.id]);
      setModals(prev => ({ ...prev, markAsSet: false }));
    } else if (data.is_unique === true) {
      markAsOriginalMutation.mutate([listing.id]);
      setModals(prev => ({ ...prev, markAsOriginal: false }));
    } else if (data.reject === true) {
      rejectListingMutation.mutate(listing.id);
      setModals(prev => ({ ...prev, reject: false }));
    }
  };

  const handleEdit = () => {
    setModals(prev => ({ ...prev, editListing: true }));
  };

  const onEditModalClose = () => {
    setModals(prev => ({ ...prev, editListing: false }));
  };

  const onListingEditFieldModalOpen = (field: ListingEditFormOption) => {
    setModals(prev => ({ ...prev, listingEditFieldModal: field }));
  };

  const onListingEditFieldModalClose = () => {
    setModals(prev => ({ ...prev, listingEditFieldModal: undefined }));
  };

  const onArtistClick = () => {
    if (artist) {
      navigate(NavigationService.getArtistDetailsPath(artist.id, artist.name));
    }
  };

  const handleEditFormSubmit = (data: ListingEditFormData) => {
    if (listing) {
      updateListingMutation.mutate(
        { id: listing.id, data: data as JSONObject },
        {
          onSuccess: () => {
            onEditModalClose();
          },
        }
      );
    }
  };

  const createItemPreview = useMemo(() => (listing ? createPreviewItemFromListing(listing) : null), [listing]);

  const getDefaultFormData = (): ListingEditFormData | undefined => {
    if (!listing) return undefined;

    return {
      name: listing.name,
      rawPrice: listing.rawPrice,
      date: listing.date ? listing.date.format('YYYY-MM-DD') : undefined,
      edition: listing.edition,
      dimensions: listing.dimensions,
      estimateLow: listing.estimate?.low,
      estimateHigh: listing.estimate?.high,
      materialIds: listing.materialIds,
      type: listing.type,
    };
  };

  const onCreateItemModalClose = () => {
    setModals(prev => ({ ...prev, createItem: false }));
    createItemFromListingMutation.reset();
  };

  const handleCreateItemSubmit = async () => {
    if (!listing) return;

    createItemFromListingMutation.mutate(listing.id);
  };

  const onMigrateImageModalOpen = () => {
    setModals(prev => ({ ...prev, migrateImage: true }));
  };

  const onMigrateImageModalClose = () => {
    setModals(prev => ({ ...prev, migrateImage: false }));
  };

  const handleMigrateImageSubmit = (entity: ItemModel | ListingModel | ArtistModel, newImageUrl: string) => {
    if (!listing) {
      onMigrateImageModalClose();
      return;
    }

    const mutation = isDemoted ? migrateDemotedListingImageMutation : migrateListingImageMutation;

    mutation.mutate(
      { id: listing.id, imageUrl: newImageUrl },
      {
        onSuccess: () => {
          onMigrateImageModalClose();
        }
      }
    );
  };

  const handleUnlink = () => {
    unlinkListingMutation.mutate({ itemId: associatedItem.id, listingId: listing.id });
  };

  useEffect(() => {
    if (createItemFromListingMutation.isSuccess && listing) {
      const newItemId = createItemFromListingMutation.data;
      onCreateItemModalClose();
      navigate(NavigationService.getItemDetailsPath(newItemId, listing.name));
    }
  }, [createItemFromListingMutation.isSuccess, createItemFromListingMutation.data, listing, navigate]);

  useEffect(() => {
    if (createItemFromListingMutation.isError) {
      createItemFromListingMutation.reset();
    }
  }, [createItemFromListingMutation.isError, createItemFromListingMutation.error]);

  if (listingLoading) {
    return <Box mt="100px" display="flex" justifyContent="center"><Loader /></Box>;
  }

  return (
    <Box maxWidth="1200px" width="100%" paddingTop={['80px', '100px', '100px']} justifySelf="center" minHeight="100vh" mx="auto">
      <ListingContainer
        listing={listing}
        artist={artist}
        onEditClick={handleEdit}
        onViewSourceClick={handleViewSource}
        onGroupingClick={onItemSuggestionWizardOpen}
        onCreateItemClick={handleCreateItem}
        onMarkAsFakeClick={handleMarkAsFake}
        onMarkAsSetClick={handleMarkAsSet}
        onMarkAsOriginalClick={handleMarkAsOriginal}
        onRejectClick={handleReject}
        onUnlinkClick={handleUnlink}
        unlinkLoading={unlinkListingMutation.isPending}
        onArtistClick={onArtistClick}
        onMigrateImageClick={onMigrateImageModalOpen}
        migrateImageLoading={isDemoted ? migrateDemotedListingImageMutation.isPending : migrateListingImageMutation.isPending}
      />

      {renderListingInfoSlider()}
      <Box mt="40px">
        {renderTabSection()}
      </Box>

      {/* Modals */}
      {renderModals()}
    </Box>
  );
}
